import * as React from "react";

import type {ShipmentDetail} from "./EmissionsCalculatorReducer";
import type {ShipmentEntryInputData} from "./ShipmentEntryInputGroup";
import {TRANSPORTATION_MODES, WEIGHT_UNITS} from "./constants/dropdown-options";

import type {WeightUnits, TransportationModeType} from "./types";

import ReeferIcon from "../../svg/chilled.svg";
import arrowIcon from "../../svg/arrow-left.svg";
import closeIcon from "../../svg/close.svg";

import Icon from "../icon/Icon";
import {css} from "../../utils/utils";

import * as styles from "./styles/calculated-shipment-row.module.scss";

interface Props {
  shipmentDetail: ShipmentDetail;
  highlight?: boolean;
  hideCloseButton?: boolean;
  onClose: (id: string) => void;
}

enum PORT_TYPES {
  ORIGIN = "origin",
  DESTINATION = "destination",
}

const parseMode = (mode: TransportationModeType): string => {
  switch (mode) {
    case TRANSPORTATION_MODES.oceanFcl:
      return "Ocean FCL";
    case TRANSPORTATION_MODES.oceanLcl:
      return "Ocean LCL";
    case TRANSPORTATION_MODES.air:
      return "Air";
    case TRANSPORTATION_MODES.truck:
      return "Truck";
    case TRANSPORTATION_MODES.rail:
      return "Rail";
    default:
      return "";
  }
};

const getAddressOrPort = (
  data: ShipmentEntryInputData,
  portType: PORT_TYPES
): string | undefined => {
  const {
    transportationMode,
    originPortName,
    destinationPortName,
    originAddress,
    destinationAddress,
  } = data;

  if (
    transportationMode === TRANSPORTATION_MODES.truck ||
    transportationMode === TRANSPORTATION_MODES.rail
  ) {
    return portType === PORT_TYPES.ORIGIN
      ? `${originAddress?.city}, ${originAddress?.state}`
      : `${destinationAddress?.city}, ${destinationAddress?.state}`;
  }

  return portType === PORT_TYPES.ORIGIN ? originPortName : destinationPortName;
};

const parseWeightUnit = (unit: WeightUnits): string => {
  switch (unit) {
    case WEIGHT_UNITS.lb:
      return "lbs";
    case WEIGHT_UNITS.kg:
      return "kgs";
    default:
      return "teus";
  }
};

const CalculatedShipmentRow = (props: Props) => {
  const {
    shipmentDetail,
    highlight = false,
    hideCloseButton = false,
    onClose,
  } = props;

  const [isDeleting, setIsDeleting] = React.useState<boolean>(false);

  const data = shipmentDetail.data;

  const handleDelete = React.useCallback(() => {
    setIsDeleting(true);
    setTimeout(() => {
      onClose(shipmentDetail.id);
    }, 500);
  }, [onClose, shipmentDetail.id]);

  const rowClass = highlight ? styles.calculatedShipmentRow__highlighted : "";

  return (
    <table
      className={css(
        styles.calculatedShipmentRow__table,
        isDeleting ? styles.slideOut : ""
      )}
    >
      <tbody>
        <tr className={css(styles.calculatedShipmentRow__row, rowClass)}>
          <td className={styles.calculatedShipmentRow__restCell}>
            {parseMode(data.transportationMode)}
          </td>

          <td className={styles.calculatedShipmentRow__iconCell}>
            {data.isReefer && (
              <Icon
                svg={ReeferIcon}
                className={styles.calculatedShipmentRow__reefer}
              />
            )}
          </td>

          <td className={styles.calculatedShipmentRow__originDestCell}>
            {getAddressOrPort(data, PORT_TYPES.ORIGIN)}
          </td>

          <td className={styles.calculatedShipmentRow__iconCell}>
            <Icon
              svg={arrowIcon}
              className={styles.calculatedShipmentRow__arrow}
            />
          </td>

          <td className={styles.calculatedShipmentRow__originDestCell}>
            {getAddressOrPort(data, PORT_TYPES.DESTINATION)}
          </td>

          <td className={styles.calculatedShipmentRow__restCell}>
            {data.weight.value} {parseWeightUnit(data.weight.unit)}
          </td>

          <td className={styles.calculatedShipmentRow__emissionsCell}>
            <p>{shipmentDetail.calculatedEmissions?.toFixed(2)} TCO2e</p>
          </td>

          {!hideCloseButton && (
            <td className={styles.calculatedShipmentRow__iconCell}>
              <button
                onClick={handleDelete}
                className={styles.calculatedShipmentRow__closeBtnDiv}
              >
                <Icon
                  svg={closeIcon}
                  className={styles.calculatedShipmentRow__close}
                />
              </button>
            </td>
          )}
        </tr>
      </tbody>
    </table>
  );
};

export default CalculatedShipmentRow;
